import React, { useCallback, useEffect, useState } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import sanitizeHtml from 'sanitize-html';

import InlineEdit from '@atlaskit/inline-edit';
import Textfield from '@atlaskit/textfield';

import RenderedTitle from 'src/components/rendered-title';
import { RenderedContent } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import { UPDATE_TITLE } from 'src/redux/pull-request/actions';
import { getIsPullRequestTitleUpdatePending } from 'src/redux/pull-request/selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';

const ReadViewWrapper = styled.div`
  padding-top: 6px;
  padding-bottom: 6px;
  padding-right: 6px;
  letter-spacing: 0;
`;

const newlineOrTabRegex = /[\n\r\t]+/g;

const messages = {
  editButtonLabel: {
    id: 'frontbucket.repository.pullrequest.editableTitle.editButtonLabel',
    description:
      'Text for accessibility label for button which is used to enter edit view from keyboard.',
    defaultMessage: 'Edit pull request title',
  },
  confirmButtonLabel: {
    id: 'frontbucket.repository.pullrequest.editableTitle.confirmButtonLabel',
    description:
      'Text for accessibility label for the confirm action button.    ',
    defaultMessage: 'Confirm pull request title changes',
  },
  cancelButtonLabel: {
    id: 'frontbucket.repository.pullrequest.editableTitle.cancelButtonLabel',
    description:
      'Text for accessibility label for the cancel action button.    ',
    defaultMessage: 'Cancel editing pull request title',
  },
};

export type EditableTitleProps = {
  renderedTitle: RenderedContent;
};

export const EditableTitle = ({ renderedTitle }: EditableTitleProps) => {
  const rawTitle = renderedTitle.raw.replace(newlineOrTabRegex, ' ');
  const [textFieldValue, setTextFieldValue] = useState(rawTitle);
  const dispatch = useDispatch();
  const intl = useIntl();
  const isPrTitleUpdatePending = useSelector(
    getIsPullRequestTitleUpdatePending
  );
  const currentUser = useSelector(getCurrentUser);

  const beforeUnloadHandler = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault();
    // Necessary for Google Chrome
    event.returnValue = null;
  }, []);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextFieldValue(e.target.value);
    window.addEventListener('beforeunload', beforeUnloadHandler, false);
  };

  useEffect(() => {
    return function cleanup() {
      window.removeEventListener('beforeunload', beforeUnloadHandler, false);
    };
  }, [beforeUnloadHandler]);

  return currentUser ? (
    <InlineEdit
      defaultValue={rawTitle}
      isRequired
      keepEditViewOpenOnBlur
      readViewFitContainerWidth
      editButtonLabel={intl.formatMessage(messages.editButtonLabel)}
      confirmButtonLabel={intl.formatMessage(messages.confirmButtonLabel)}
      cancelButtonLabel={intl.formatMessage(messages.cancelButtonLabel)}
      editView={fieldProps => (
        <Textfield
          {...fieldProps}
          // disable textfield to prevent editing while update is in-progress
          isDisabled={isPrTitleUpdatePending}
          autoFocus
          onChange={handleOnChange}
          value={textFieldValue}
          css={{
            fontSize: '24px',
            fontWeight: 500,
            padding: '1px 0px 1px 0px',
            letterSpacing: '0px',
            height: 'inherit',
          }}
        />
      )}
      readView={() => {
        let renderedContent: RenderedContent;

        // While request is being processed, we want to optimistically display the updated
        // version of the title. If the request fails, the UI will show an error flag
        // and return to the original title.
        if (isPrTitleUpdatePending) {
          // UGC needs to be escaped, this may not match server's sanitized text but is uncommon usage
          // tags will always be escaped while attrs will not as this content is placed in span
          // and won't be interpreted as HTML to render
          const sanitizedContent = sanitizeHtml(textFieldValue, {
            allowedTags: [],
            allowedAttributes: false,
            disallowedTagsMode: 'escape',
          });
          renderedContent = {
            ...renderedTitle,
            raw: textFieldValue,
            html: `<p>${sanitizedContent}</p>`,
          };
        } else {
          renderedContent = renderedTitle;
          setTextFieldValue(rawTitle);
        }

        window.removeEventListener('beforeunload', beforeUnloadHandler, false);

        return (
          <ReadViewWrapper>
            <RenderedTitle renderedContent={renderedContent} />
          </ReadViewWrapper>
        );
      }}
      onConfirm={() => {
        dispatch({
          type: UPDATE_TITLE.REQUEST,
          payload: { title: textFieldValue },
        });
      }}
    />
  ) : (
    <ReadViewWrapper>
      <RenderedTitle renderedContent={renderedTitle} />
    </ReadViewWrapper>
  );
};
